import { INVENTORY_KINDS, type InventoryKind } from "@loot-os/fulfillment-core/catalog/inventory";

/** Cards on one page. The inventory query accepts at most this many. */
export const INVENTORY_PAGE_SIZE = 200;

/** Where a download stops. Past this, the file says it was cut off. */
export const INVENTORY_DOWNLOAD_LIMIT = 5000;

/** The Retool category select, a tag on the item. Blank counts every category. */
export const INVENTORY_CATEGORIES = [
  ["POKEMON", "Pokemon"],
  ["MTG", "MTG"],
  ["ONE_PIECE", "One Piece"],
  ["RIFTBOUND", "Riftbound"],
  ["LORCANA", "Lorcana"],
  ["HIGH_ERROR", "High error rate"],
  ["YUGIOH", "Yu-Gi-Oh"],
  ["SPORTS", "Sports"],
  ["BASEBALL", "Baseball"],
  ["BASKETBALL", "Basketball"],
  ["FOOTBALL", "Football"],
  ["HOCKEY", "Hockey"],
  ["SOCCER", "Soccer"],
] as const;

export const INVENTORY_KIND_LABELS: Record<InventoryKind, string> = {
  SINGLE: "Single",
  SLABBED: "Slabbed",
  SEALED: "Sealed",
  OTHER: "Other",
  UNCLASSIFIED: "Unclassified custom",
};

export { INVENTORY_KINDS, type InventoryKind };

const defaultBounds = inventoryPriceBounds("0", "99999");
if (typeof defaultBounds === "string") throw new Error(defaultBounds);
/** Retool's opening range: above $0 and below $99,999. */
export const DEFAULT_INVENTORY_BOUNDS: InventoryPriceBounds = defaultBounds;

export interface InventoryPriceBounds {
  valueMin: number;
  valueMax: number;
}

/**
 * Retool's price filter, as cents: strictly above the minimum and strictly below the maximum.
 * Blank is not a bound. A row with no value is left out because both bounds are always set.
 */
export function inventoryPriceBounds(
  minDollars: string,
  maxDollars: string,
): InventoryPriceBounds | string {
  const min = dollarsToCents(minDollars);
  const max = dollarsToCents(maxDollars);
  if (min === "invalid") return "Enter a minimum price, like 0 or 12.50.";
  if (max === "invalid") return "Enter a maximum price, like 99999.";
  if (min === null || max === null) return "Enter a minimum and a maximum price.";
  const valueMin = min + 1;
  const valueMax = max - 1;
  if (valueMin > valueMax) return "The maximum price has to be above the minimum.";
  return { valueMin, valueMax };
}

/** A whole number, or null when the field is blank. Anything else is invalid. */
export function inventoryQuantityMin(raw: string): number | null | "invalid" {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (!/^-?\d+$/.test(trimmed)) return "invalid";
  return Number(trimmed);
}

export interface InventoryCsvRow {
  name: string;
  id: string;
  quantity: number;
  withdrawingCount: number;
  value: number | null;
  productId: string | null;
  rocaId: string | null;
}

/** The Retool download: name, id, quantity, withdrawing, price in dollars, TCGplayer id, Roca id. */
export function inventoryCsv(rows: InventoryCsvRow[]): string {
  const header = [
    "name",
    "id",
    "quantity",
    "withdrawingCount",
    "price",
    "tcgPlayerId",
    "tcgPlayerVendorId",
  ];
  const lines = rows.map((row) =>
    [
      row.name,
      row.id,
      String(row.quantity),
      String(row.withdrawingCount),
      row.value === null ? "" : (row.value / 100).toFixed(2),
      row.productId ?? "",
      row.rocaId ?? "",
    ]
      .map(csvCell)
      .join(","),
  );
  return [header.join(","), ...lines].join("\n");
}

/**
 * One color per printing, on the dark inventory page. A counter tells Holofoil from Reverse
 * Holofoil, and a 1st Edition from an Unlimited, without reading the word. A printing this list
 * does not name still gets a stable color of its own from the leftover set.
 */
const PRINTING_BADGE: Record<string, string> = {
  Normal: "border-border bg-muted text-muted-foreground",
  Holofoil: "border-amber-500/50 bg-amber-500/15 text-amber-300",
  "Reverse Holofoil": "border-sky-500/50 bg-sky-500/15 text-sky-300",
  "Unlimited Holofoil": "border-violet-500/50 bg-violet-500/15 text-violet-300",
  "1st Edition Holofoil": "border-rose-500/50 bg-rose-500/15 text-rose-300",
  "1st Edition": "border-emerald-500/50 bg-emerald-500/15 text-emerald-300",
  Unlimited: "border-orange-500/50 bg-orange-500/15 text-orange-300",
  Foil: "border-fuchsia-500/50 bg-fuchsia-500/15 text-fuchsia-300",
  "Cold Foil": "border-cyan-400/50 bg-cyan-400/15 text-cyan-300",
};

const OTHER_PRINTING_BADGES = [
  "border-lime-500/50 bg-lime-500/15 text-lime-300",
  "border-teal-500/50 bg-teal-500/15 text-teal-300",
  "border-indigo-500/50 bg-indigo-500/15 text-indigo-300",
  "border-yellow-400/50 bg-yellow-400/15 text-yellow-200",
] as const;

export function printingBadgeClass(printing: string): string {
  const known = PRINTING_BADGE[printing];
  if (known) return known;
  const extra = OTHER_PRINTING_BADGES[printingHash(printing) % OTHER_PRINTING_BADGES.length];
  return extra ?? "border-border bg-muted text-muted-foreground";
}

function printingHash(value: string): number {
  let hash = 0;
  for (const char of value) hash = (Math.imul(hash, 31) + char.charCodeAt(0)) >>> 0;
  return hash;
}

function csvCell(value: string): string {
  return `"${value.replaceAll('"', '""')}"`;
}

function dollarsToCents(raw: string): number | null | "invalid" {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(trimmed);
  const whole = match?.[1];
  if (!match || whole === undefined) return "invalid";
  const fraction = (match[2] ?? "").padEnd(2, "0");
  return Number(whole) * 100 + Number(fraction);
}
